/**
 * Acceso Corto — Single Source of Truth del código de acceso de un solo uso.
 *
 * El link que el operador comparte por WhatsApp para que un promotor (o, a
 * futuro, una caja/TV web) entre por primera vez. Antes era un hex de 64
 * caracteres en `?acceso=`; ahora es un CÓDIGO CORTO de 6 dígitos en el path:
 *
 *     partner.<brand>/481902
 *
 * Por qué 6 dígitos y no más: el código lo TOCA UN HUMANO (lo dicta por
 * teléfono, lo teclea en la app instalada cuando iOS no puede pasarlo solo).
 * Como los códigos se RECICLAN (un código vencido/usado vuelve al pozo y se
 * reasigna), nunca hay millones vivos a la vez — bastan decenas. Con un espacio
 * de 1.000.000, un solo uso, vencimiento y rate-limit en el canje, 6 dígitos es
 * de sobra seguro a esta escala. El público (promotores, gama baja) lo trata
 * como el código de Instagram/WhatsApp: teclado numérico, sin deletrear, sin
 * confundir mayúsculas ni "¿es O o cero?".
 *
 * CONVIVENCIA, NO REEMPLAZO: los links hex64 ya emitidos a cuentas existentes
 * siguen siendo válidos. El canje acepta AMBOS formatos en cascada (hex64
 * primero, corto después). El device-token persistente que se mintea al canjear
 * sigue siendo el hex64 de siempre — el código corto es solo la LLAVE DE
 * ENTRADA, se canjea por el device-token largo. Adentro nada cambia.
 *
 * Este módulo CONGELA el contrato cruzado entre las capas que lo tocan:
 *   - Backend (mint): genera el código con generarCodigoCortoAcceso().
 *   - Backend (canje): valida con ACCESO_HEX64_REGEX || CODIGO_CORTO_REGEX.
 *   - Front (canje): extrae el token de lo que sea que llegue con extraerTokenAcceso().
 *   - Admin (link): arma partner.<brand>/<codigo>.
 *
 * Uso:
 *   - ESM:     import { extraerTokenAcceso } from './acceso-corto.js'
 *   - Backend: const { extraerTokenAcceso } = require('./acceso-corto')
 *   - Browser: <script src="acceso-corto.js"> → window.AccesoCorto
 */

// Longitud del código corto. Parametrizada para que un futuro 6→7/8 no obligue a
// cazar literales: cambiar acá y el regex se recompone solo.
export const LONGITUD_CODIGO_CORTO = 6;

// El código corto es exactamente N dígitos decimales (0-9). Solo números: el
// nini lo teclea en el teclado numérico, se dicta sin deletrear, cero ambigüedad
// de letras. Mismo regex que usa el backend en el fail-fast del canje.
export const CODIGO_CORTO_REGEX = new RegExp(`^\\d{${LONGITUD_CODIGO_CORTO}}$`);

// El token legacy: 64 caracteres hex en minúscula (el que generaba generateToken
// antes de los códigos cortos). Sigue vivo para los links ya emitidos.
export const ACCESO_HEX64_REGEX = /^[0-9a-f]{64}$/;

// Cap defensivo al texto que el promotor pega en la caja (puede pegar el mensaje
// entero de WhatsApp). Anti-DoS antes de correr regex sobre el string.
const MAX_TEXTO_PEGADO = 4096;

/**
 * Extrae el token de acceso de CUALQUIER cosa que pegue un promotor: la URL
 * completa, el dominio + código, solo el código, o el mensaje de bienvenida
 * entero de WhatsApp. "Que entre porque entre" — el backend es la autoridad
 * final (el gate atómico rechaza un código inexistente).
 *
 * Cascada deliberada: el hex64 legacy gana SIEMPRE. Solo si NO hay un hex64 en
 * el texto se busca el código corto. Así un link viejo (?acceso=<hex64>) nunca
 * se confunde con un código corto, y nunca se brickea un link ya emitido.
 *
 * @param {string} textoPegado
 * @returns {{ tipo: 'hex64'|'corto', valor: string } | null}
 */
export function extraerTokenAcceso(textoPegado) {
    if (typeof textoPegado !== 'string') return null;
    // Cap + normalizar separadores invisibles (NBSP, zero-width) que el teclado
    // del nini o el copiado de WhatsApp pueden meter, antes de buscar dígitos.
    const texto = textoPegado
        .slice(0, MAX_TEXTO_PEGADO)
        .replace(/[ ​-‍﻿]/g, '')
        .trim();
    if (!texto) return null;

    // (1) hex64 legacy: con o sin prefijo acceso=, en cualquier parte del texto.
    // Frontera DURA a carácter hex en ambos lados (lookbehind/lookahead negativos):
    // sin esto, una cadena de 65+ hex matchearía una subcadena de 64 corrida y
    // devolvería un token equivocado. Exactamente 64 hex aislados o nada.
    const mHex = texto.match(/(?:acceso=)?(?<![0-9a-f])([0-9a-f]{64})(?![0-9a-f])/i);
    if (mHex) return { tipo: 'hex64', valor: mHex[1].toLowerCase() };

    // (2a) código corto EN POSICIÓN DE PATH: los N dígitos que siguen a una "/"
    //      (partner.<brand>/481902, o el path pelado /481902). Es el caso fuerte —
    //      NO se confunde con otros números del mensaje (un email maria123456@,
    //      un monto, un teléfono) que NO van tras una barra. Frontera dura a dígito.
    const rePath = new RegExp(`/(\\d{${LONGITUD_CODIGO_CORTO}})(?!\\d)`, 'g');
    let codigoEnPath = null;
    for (const m of texto.matchAll(rePath)) codigoEnPath = m[1];
    if (codigoEnPath) return { tipo: 'corto', valor: codigoEnPath };

    // (2b) fallback: el promotor pegó SOLO el código (sin URL). Exactamente N dígitos
    //      aislados en todo el texto. Si hay varios números sueltos no podemos
    //      desambiguar — tomamos el primero aislado de longitud exacta.
    const reSuelto = new RegExp(`(?<!\\d)(\\d{${LONGITUD_CODIGO_CORTO}})(?!\\d)`);
    const mSuelto = texto.match(reSuelto);
    if (mSuelto) return { tipo: 'corto', valor: mSuelto[1] };

    return null;
}

/**
 * Genera un código corto aleatorio criptográficamente seguro. NO garantiza
 * unicidad: el llamador (backend) debe insertarlo bajo UNIQUE y reintentar ante
 * colisión con un código VIVO. Recibe la función de random por inyección para
 * no acoplar este módulo SSOT a crypto de Node (el backend pasa crypto.randomInt;
 * en test se puede pasar un mock determinista).
 *
 * @param {(maxExclusivo:number)=>number} randomIntExclusivo  ej. n => crypto.randomInt(n)
 * @returns {string} código de LONGITUD_CODIGO_CORTO dígitos, con ceros a la izquierda
 */
export function generarCodigoCortoAcceso(randomIntExclusivo) {
    if (typeof randomIntExclusivo !== 'function') {
        throw new Error(
            '[acceso-corto] generarCodigoCortoAcceso requiere una función de random (ej. n => crypto.randomInt(n))'
        );
    }
    const espacio = Math.pow(10, LONGITUD_CODIGO_CORTO);
    const n = randomIntExclusivo(espacio);
    return String(n).padStart(LONGITUD_CODIGO_CORTO, '0');
}

export const AccesoCorto = {
    LONGITUD_CODIGO_CORTO,
    CODIGO_CORTO_REGEX,
    ACCESO_HEX64_REGEX,
    extraerTokenAcceso,
    generarCodigoCortoAcceso,
};

export default AccesoCorto;

// ═══════════════════════════════════════════════════════════════════════════
// BACKWARD COMPATIBILITY (triple consumo: ESM / CommonJS / browser)
// ═══════════════════════════════════════════════════════════════════════════

if (typeof window !== 'undefined') {
    window.AccesoCorto = AccesoCorto;
}

if (typeof module !== 'undefined' && module.exports) {
    module.exports = AccesoCorto;
    module.exports.AccesoCorto = AccesoCorto;
}
